import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NzMessageRef } from 'ng-zorro-antd/message';
import { NotificationAlertService } from 'shared/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TALLY_SYNC_HISTORY_DAYS, TallyMasterSyncKind, TallyMasterSyncRunModel } from '../../Models/TallyModel';
import { TallyService } from '../../Services/tally.service';
import { TallySyncRunTableComponent } from '../tally-sync-run-table/TallySyncRunTable.component';

/** The most refreshes the dialog lists. */
export const TALLY_SYNC_HISTORY_SHOWN = 50;

/**
 * The Tally Sync History button and its dialog, for the pages that refresh a Tally list:
 * ledger refreshes on the supplier list, godown refreshes on the department and store
 * pages. The page shows the button while Tally is switched on for the company; the
 * server lets anyone with View on that page read the list's history.
 */
@Component({
  selector: 'app-tally-sync-history',
  standalone: true,
  imports: [NzAlertModule, NzButtonModule, NzIconModule, NzModalModule, NzTooltipModule, TallySyncRunTableComponent],
  template: `
    <button nz-button nzType="default" type="button" (click)="open()"
      nz-tooltip [nzTooltipTitle]="'Refreshes of the ' + listName + ' list from Tally in the last ' + days + ' days'">
      <span nz-icon nzType="history" nzTheme="outline"></span> Tally Sync History
    </button>
    <nz-modal [(nzVisible)]="isOpen" [nzTitle]="'Tally Sync History: ' + listTitle" nzWidth="min(1100px, 95vw)"
      [nzFooter]="footer" (nzOnCancel)="close()">
      <ng-container *nzModalContent>
        <nz-alert nzType="info" nzShowIcon class="history-note"
          [nzMessage]="'Refreshes from the last ' + days + ' days are kept; older ones are removed. Newest first, up to ' + shown + '.'">
        </nz-alert>
        @if (errorText) {
          <nz-alert nzType="warning" nzShowIcon class="history-note" [nzMessage]="errorText"></nz-alert>
        } @else {
          <app-tally-sync-run-table [runs]="runs" [showKind]="false"
            [emptyText]="isLoading ? 'Loading refresh history…' : 'No refresh in the last ' + days + ' days.'">
          </app-tally-sync-run-table>
        }
      </ng-container>
      <ng-template #footer>
        <button nz-button nzType="default" type="button" (click)="load()" [disabled]="isLoading"
          nz-tooltip nzTooltipTitle="Read the history again">
          <span nz-icon nzType="reload" nzTheme="outline"></span> Reload
        </button>
        <button nz-button nzType="primary" type="button" (click)="close()">Close</button>
      </ng-template>
    </nz-modal>
  `,
  styles: [`.history-note { margin-bottom: 12px; }`]
})
export class TallySyncHistoryComponent implements OnDestroy {
  @Input({ required: true }) kind: TallyMasterSyncKind;

  readonly days = TALLY_SYNC_HISTORY_DAYS;
  readonly shown = TALLY_SYNC_HISTORY_SHOWN;

  isOpen = false;
  isLoading = false;
  runs: TallyMasterSyncRunModel[] = [];
  errorText: string | null = null;
  private request: Subscription | null = null;
  private loadingRef: NzMessageRef | null = null;

  constructor(private tallyService: TallyService, private notification: NotificationAlertService) {}

  get listName(): string {
    return this.kind === 'Ledgers' ? 'ledger' : this.kind === 'Godowns' ? 'godown' : 'stock item';
  }

  get listTitle(): string {
    return this.kind === 'StockItems' ? 'Stock items' : this.kind;
  }

  open(): void {
    this.isOpen = true;
    this.load();
  }

  close(): void {
    this.isOpen = false;
    this.request?.unsubscribe();
    this.clearLoading();
  }

  load(): void {
    this.request?.unsubscribe();
    this.isLoading = true;
    this.errorText = null;
    this.runs = [];
    this.clearLoading();
    this.loadingRef = this.notification.loadingMessage('Loading Tally sync history…');
    this.request = this.tallyService.getMasterSyncRuns(this.kind, TALLY_SYNC_HISTORY_SHOWN).subscribe({
      next: runs => {
        this.runs = runs ?? [];
        this.isLoading = false;
        this.clearLoading();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.clearLoading();
        this.errorText = TallySyncHistoryComponent.describeError(error);
      }
    });
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.clearLoading();
  }

  private clearLoading(): void {
    if (this.loadingRef) {
      this.notification.removeMessage(this.loadingRef);
      this.loadingRef = null;
    }
  }

  static describeError(error: HttpErrorResponse | null): string {
    if (error?.status === 403) {
      return 'You do not have access to this history. Ask your administrator for View access to this page, or check that Tally is switched on for this company.';
    }
    return 'The history could not be loaded. Press Reload to try again.';
  }
}
